/*
 * mermaid 流程图块（使用模块内 vendor 文件）
 * Schema: 标准 mermaid 语法字符串
 *
 * mermaid 各版本都不能干净打包（ESM only / .mjs / 新语法），
 * 改为从 vendor/mermaid.min.js.txt 注入运行时。
 */
import React, { useEffect, useRef, useState } from 'react'
import { MERMAID_SOURCE, injectScriptFromSource } from '../../vendor'
import './MermaidBlock.scss'

interface MermaidBlockProps {
  code: string
}

let mermaidLib: any = null
let mermaidLoading: Promise<any> | null = null

function loadMermaid(): Promise<any> {
  if (mermaidLib) return Promise.resolve(mermaidLib)
  if (mermaidLoading) return mermaidLoading

  mermaidLoading = injectScriptFromSource('mermaid', MERMAID_SOURCE).then(
    () => {
      mermaidLib = (window as any).mermaid
      if (!mermaidLib) throw new Error('mermaid 全局对象未注入')
      mermaidLib.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'loose',
        fontFamily: '-apple-system, "PingFang SC", sans-serif'
      })
      return mermaidLib
    }
  )
  return mermaidLoading
}

let mermaidIdCounter = 0

export const MermaidBlock: React.FC<MermaidBlockProps> = ({ code }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [svg, setSvg] = useState<string>('')
  // 是否已经成功渲染过至少一次。流式中 code 还不完整 → mermaid.render 抛错
  // 是常态，如果每次抛错都切到 error 态，图会在"加载中→失败→正常"之间反复闪。
  // 有过成功结果就保留旧 svg，只有从没成功过才显示错误占位。
  const hasRenderedRef = useRef(false)

  useEffect(() => {
    let mounted = true
    loadMermaid()
      .then(async (mermaid) => {
        if (!mounted) return
        const id = `aiClerk-mermaid-${++mermaidIdCounter}`
        try {
          const result = await mermaid.render(id, code.trim())
          if (mounted) {
            setSvg(typeof result === 'string' ? result : result.svg)
            setError(null)
            hasRenderedRef.current = true
          }
        } catch (e) {
          // 已经成功渲染过 → 保留上一次的 svg，不闪 error（流式 partial 语法常态）
          if (mounted && !hasRenderedRef.current) {
            setError(e instanceof Error ? e.message : String(e))
          }
        }
      })
      .catch((e: Error) => {
        if (mounted && !hasRenderedRef.current) {
          setError(`mermaid 加载失败: ${e.message}`)
        }
      })

    return () => {
      mounted = false
    }
  }, [code])

  if (error) {
    return (
      <div className="aiClerk-block-mermaid aiClerk-block-mermaid--invalid">
        🧩 Mermaid 渲染失败：{error}
        <pre className="aiClerk-block-mermaid__raw">{code}</pre>
      </div>
    )
  }

  if (!svg) {
    return (
      <div className="aiClerk-block-mermaid aiClerk-block-mermaid--loading">
        🧩 加载流程图中…
      </div>
    )
  }

  return (
    <div className="aiClerk-block-mermaid">
      <div
        ref={containerRef}
        className="aiClerk-block-mermaid__svg"
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    </div>
  )
}
